import { useState } from 'react';
import { Box, Button, Card, Flex, Icon, IconButton, Input, Separator, Text, Textarea } from '@chakra-ui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PlanItem, PlanSection } from '@/lib/types';
import { SortableList } from '@/components/sortable-list';
import ItemRow from './item-row';
import ItemSearch from './item-search';

// SortableList requires an `id: UniqueIdentifier`; PlanItem's stable
// client id is `uid` (string) — wrapped for the nested item list's
// bookkeeping only, `item` itself passes through unchanged.
interface SortableItemEntry {
  id: string;
  item: PlanItem;
}

interface SectionCardProps {
  section: PlanSection;
  index: number;
  onChange: (section: PlanSection) => void;
  onRemove: () => void;
  onViewItem: (item: PlanItem) => void;
  showItemErrors?: boolean;
}

// One plan section: number badge, name, duration, optional notes, and its
// ordered items (drag-reorder via a nested independent SortableList).
export default function SectionCard({ section, index, onChange, onRemove, onViewItem, showItemErrors }: SectionCardProps) {
  const [showSearch, setShowSearch] = useState(false);

  const updateItem = (item: PlanItem) => {
    onChange({
      ...section,
      items: section.items.map((i) => (i.uid === item.uid ? item : i)),
    });
  };

  const removeItem = (itemUid: string) => {
    onChange({ ...section, items: section.items.filter((i) => i.uid !== itemUid) });
  };

  const addItem = (item: PlanItem) => {
    onChange({ ...section, items: [...section.items, item] });
  };

  const sortableItems: SortableItemEntry[] = section.items.map((item) => ({
    id: item.uid,
    item,
  }));

  const handleReorderItems = (newItems: SortableItemEntry[]) => {
    onChange({ ...section, items: newItems.map((entry) => entry.item) });
  };

  return (
    <Card.Root>
      <Card.Body>
        <Flex align='center' gap={3} mb={3}>
          <SortableList.DragHandle />
          <Flex
            align='center'
            justify='center'
            w={7}
            h={7}
            borderRadius='full'
            bg='brand.subtle'
            color='brand.solid'
            fontWeight='700'
            fontSize='sm'
            flexShrink={0}
          >
            {index + 1}
          </Flex>
          <Input
            value={section.name}
            onChange={(e) => onChange({ ...section, name: e.target.value })}
            placeholder='Section name'
            fontWeight='600'
            flex='1'
          />
          <Input
            type='number'
            min={0}
            value={section.duration_minutes}
            onChange={(e) => onChange({ ...section, duration_minutes: parseInt(e.target.value, 10) || 0 })}
            width='20'
            textAlign='center'
          />
          <Text fontSize='xs' color='fg.muted' flexShrink={0}>
            min
          </Text>
          <IconButton
            aria-label='Remove section'
            variant='ghost'
            size='sm'
            onClick={onRemove}
            _hover={{ color: 'danger', bg: 'danger.subtle' }}
          >
            <Icon as={TrashIcon} w={4} h={4} />
          </IconButton>
        </Flex>

        <Textarea
          value={section.notes}
          onChange={(e) => onChange({ ...section, notes: e.target.value })}
          placeholder='Section notes (optional)'
          size='sm'
          rows={2}
          mb={3}
        />

        <Separator mb={3} />

        <Box mb={2}>
          <SortableList
            items={sortableItems}
            onChange={handleReorderItems}
            renderItem={({ item }) => (
              <SortableList.Item id={item.uid}>
                <ItemRow
                  item={item}
                  onChange={updateItem}
                  onRemove={() => removeItem(item.uid)}
                  onViewDetail={() => onViewItem(item)}
                  showRequiredError={showItemErrors}
                />
              </SortableList.Item>
            )}
          />
        </Box>

        {section.items.length === 0 && (
          <Text fontSize='sm' color='fg.subtle' mb={2}>
            No items yet.
          </Text>
        )}

        {showSearch ? (
          <Box mt={2}>
            <ItemSearch onAdd={addItem} onDone={() => setShowSearch(false)} />
          </Box>
        ) : (
          <Button
            variant='outline'
            size='sm'
            onClick={() => setShowSearch(true)}
          >
            <Icon as={PlusIcon} w={4} h={4} />
            Add content item
          </Button>
        )}
      </Card.Body>
    </Card.Root>
  );
}
